/**
 * The organization's servers, which of them are online, and each one's latest update report.
 *
 * The list is one fetch shared by every consumer, kept current by `servers` change frames. The
 * update reports are a fetch of their own because nothing pushes them: download and install
 * progress is current only when a reader asks for it, so asking must not refetch the list.
 *
 * Whether a server is online comes only from its `ms.<id>` status frames: online while its
 * latest frame says so, offline otherwise — before its first frame, and once the stream has been
 * down past its grace period. The statuses sit apart from the list, so a reload can never
 * overwrite one that arrived while the reload was in flight.
 */

import { atom } from 'jotai';

import { snapshotAtoms } from '../snapshotAtoms';
import { api } from '@/api';
import { isOnlineStatus } from '@/lib/resourceStatus';
import type { ServerGate } from '@/types/contract';
import { type StreamInput, StreamInputKind } from '@/types/notifications';
import type {
    FleetServer, Server, ServersSnapshot, ServerUpdateStatus,
} from '@/types/servers';

import { UPDATES_POLL_MS } from './updatesAtoms';

const PENDING: ServersSnapshot = { servers: [], isLoading: true, isLoaded: false, error: null };

const snapshot = snapshotAtoms<ServersSnapshot>(PENDING, async () => {
    return { servers: await api.listServers(), isLoading: false, isLoaded: true, error: null };
});

export const serversAtom = snapshot.value;
export const refreshServersAtom = snapshot.reload;
export const revalidateServersAtom = snapshot.revalidate;

interface UpdateReportsSnapshot {
    updateById: ReadonlyMap<string, ServerUpdateStatus>;
    isLoading: boolean;
    error: Error | null;
}

const NO_REPORTS: UpdateReportsSnapshot = { updateById: new Map(), isLoading: true, error: null };

const reports = snapshotAtoms<UpdateReportsSnapshot>(NO_REPORTS, async () => {
    // A fleet with no update reports yet is not an error: it shows no badges.
    const rows = await api.listUpdateStatus().catch((): ServerUpdateStatus[] => []);
    return {
        updateById: new Map(rows.map((row) => [row.serverId, row])),
        isLoading: false,
        error: null,
    };
}, { refreshEveryMs: UPDATES_POLL_MS });

export const serverUpdateReportsAtom = reports.value;
export const refreshServerUpdateReportsAtom = reports.reload;
export const revalidateServerUpdateReportsAtom = reports.revalidate;

const NONE_ONLINE: ReadonlySet<string> = new Set();

/** The servers whose latest status frame says online. Written only by serversStreamAtom. */
export const serverOnlineIdsAtom = atom<ReadonlySet<string>>(NONE_ONLINE);

// The rows alone, so a reload that only flips `isLoading` leaves the fleet list's identity alone
// and every memo keyed on it stays put.
const rowsAtom = atom((get): Server[] => get(serversAtom).servers);
const updateByIdAtom = atom((get): ReadonlyMap<string, ServerUpdateStatus> =>
    get(serverUpdateReportsAtom).updateById);

/**
 * Whether a server runs what the cloud holds current, which decides how a contract violation in
 * its answers is graded. A server without a report is not current.
 */
export const serverGateAtom = atom((get): ServerGate => {
    const updateById = get(updateByIdAtom);
    return (serverId: string): boolean => updateById.get(serverId)?.state === 'upToDate';
});

/** The first listed server's id, or null. A string, so a reload of the same list wakes nobody. */
export const firstServerIdAtom = atom((get): string | null => get(rowsAtom)[0]?.id ?? null);

/** serverId → its IANA zone, skipping servers that report none. Derived from the
 *  rows, so a reload that only flips `isLoading` leaves this map's identity — and every memo
 *  keyed on it — alone. The source each timestamp's display zone resolves from
 *  (see hooks/useZoneResolver). */
export const serverZoneByIdAtom = atom((get): Record<string, string> => {
    const outRecord: Record<string, string> = {};
    for (const server of get(rowsAtom)) {
        if (server.timeZone) { outRecord[server.id] = server.timeZone; }
    }
    return outRecord;
});

/** Each server with whether it is online and its latest update report. */
export const fleetServersAtom = atom((get): FleetServer[] => {
    const onlineIds = get(serverOnlineIdsAtom);
    const updateById = get(updateByIdAtom);
    return get(rowsAtom).map((server) => ({
        ...server,
        isOnline: onlineIds.has(server.id),
        updateStatus: updateById.get(server.id) ?? null,
    }));
});

/** Each server by id, with whether it is online: how a record that names its server reads it. */
export const fleetServerByIdAtom = atom((get): ReadonlyMap<string, FleetServer> => (
    new Map(get(fleetServersAtom).map((server) => [server.id, server]))
));

/** What the notifications stream means for the servers. */
export const serversStreamAtom = atom(null, (get, set, input: StreamInput): void => {
    // The gap may have hidden a roster change, and the reports may have moved on in it.
    if (input.kind === StreamInputKind.Reopened) {
        set(refreshServersAtom);
        set(refreshServerUpdateReportsAtom);
        return;
    }
    if (input.kind === StreamInputKind.Down) {
        if (get(serverOnlineIdsAtom).size > 0) {
            set(serverOnlineIdsAtom, NONE_ONLINE);
        }
        return;
    }
    const { frame } = input;
    // A server added, removed or renamed anywhere in the organization.
    if (frame.type === 'change' && frame.resource === 'servers') {
        set(refreshServersAtom);
        return;
    }
    if (frame.type !== 'serverStatusChanged') {
        return;
    }
    const { serverId } = frame;
    const isOnline = isOnlineStatus(frame.status);
    const currentIds = get(serverOnlineIdsAtom);
    // Every reconnect replays every status; one that changes nothing must wake nobody.
    if (currentIds.has(serverId) === isOnline) {
        return;
    }
    const nextIds = new Set(currentIds);
    if (isOnline) {
        nextIds.add(serverId);
    } else {
        nextIds.delete(serverId);
    }
    set(serverOnlineIdsAtom, nextIds);
});
